import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Skeleton} from '../../../../../src/components/common/skeleton';
import {
  FolderBreadcrumb,
  FolderBreadcrumbs,
  StyledAncestorFolderBreadcrumbStyleProps,
} from '../../../../../src/components/folderBreadcrumbs/folderBreadcrumbs';
import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../shared/helpers/src/interaction/interactionHelpers';
import {Icon} from '../../../../shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../shared/ui/src/styles/fontStyles';
import {UserTheme, useUserTheme} from '../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Types.
 */

interface PageHeaderProps extends InteractionComponentProps {
  title: ReactNode;
  contextDetails?: ReactNode;
  actions?: ReactNode;
  avatar?: ReactNode;

  topLevelBreadcrumb?: FolderBreadcrumb;
  isLoading?: boolean;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 32px 48px 12px 48px;
  flex-flow: column;
`;

const StyledTitleAndActionsContainerDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTitleContainerDiv = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledTitleDiv = styled.div`
  font-size: 24px;
  font-weight: ${fontWeights.black};
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledActionsContainerDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledBreadcrumbsContainerDiv = styled.div`
  margin: 0 0 5px 0;
`;

const StyledContextDetailsDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  margin: 0 0 3px 4px;
`;

const StyledAvatarDetailsDiv = styled.div`
  margin: 0 0 3px 8px;
`;

const StyledChevronIcon = styled(Icon)`
  display: inline-block;
  vertical-align: text-bottom;
  margin: 2px 2px 1px;
  color: ${(p) => p.theme.greys.shade60};
`;

const commonBreadcrumbStyles = css`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 3px;
  grid-auto-columns: auto;
  align-items: center;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  min-height: 16px;
`;

const StyledFolderBreadcrumbSpan = styled.span<StyledAncestorFolderBreadcrumbStyleProps>`
  min-width: fit-content;
  color: ${(p) => p.theme.greys.shade60};
  ${commonBreadcrumbStyles};

  &:hover {
    color: ${(p) => p.theme.greys.shade80};

    > div,
    > svg {
      opacity: 1;
    }
  }

  > div,
  > svg {
    opacity: ${(p) => (p.$isSolitaryTopLevelBreadcrumb ? '1' : '0.4')};
  }
`;

/*
 * Component.
 */

export const PageHeader: FC<PageHeaderProps> = (props) => {
  const {title, contextDetails, actions, avatar, topLevelBreadcrumb, isLoading, interactionId} = props;
  const theme = useUserTheme();
  if (isLoading) {
    return (
      <StyledHeaderDiv>
        <StyledBreadcrumbsContainerDiv>
          <Skeleton height="12px" width="80px" />
        </StyledBreadcrumbsContainerDiv>
        <StyledTitleAndActionsContainerDiv>
          <StyledTitleContainerDiv>
            <Skeleton height="28px" width="250px" />
          </StyledTitleContainerDiv>
        </StyledTitleAndActionsContainerDiv>
      </StyledHeaderDiv>
    );
  }

  return (
    <StyledHeaderDiv {...buildInteractionIdProps(interactionId)}>
      {maybeRenderBreadcrumbs(theme, topLevelBreadcrumb)}
      <StyledTitleAndActionsContainerDiv>
        <StyledTitleContainerDiv>
          <StyledTitleDiv>{title}</StyledTitleDiv>

          {avatar && <StyledAvatarDetailsDiv>{avatar}</StyledAvatarDetailsDiv>}
          {contextDetails && <StyledContextDetailsDiv>{contextDetails}</StyledContextDetailsDiv>}
        </StyledTitleContainerDiv>
        {actions && <StyledActionsContainerDiv>{actions}</StyledActionsContainerDiv>}
      </StyledTitleAndActionsContainerDiv>
    </StyledHeaderDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderBreadcrumbs(theme: UserTheme, topLevelBreadcrumb?: FolderBreadcrumb) {
  if (!topLevelBreadcrumb) {
    return null;
  }
  return (
    <StyledBreadcrumbsContainerDiv>
      <FolderBreadcrumbs
        styles={{
          ancestorSpan: StyledFolderBreadcrumbSpan,
          currentFolderSpan: StyledFolderBreadcrumbSpan,
          showSeparatorAfterTopLevel: true,
          showSeparatorAfterLastBreadcrumb: false,
          breadcrumbSeparator: <StyledChevronIcon name="chevronRightTiny" color={theme.greys.shade60} />,
        }}
        topLevelBreadcrumb={topLevelBreadcrumb}
        folderPathBreadcrumbs={[]}
      />
    </StyledBreadcrumbsContainerDiv>
  );
}
